/**
 * `SelectionContextChip` — the chip rendered below the composer
 * textarea (above the toolbar) when the lawyer has selected text in a
 * viewer surface. Reads `useSelectionContextStore`; shows the actual
 * selected text (wrapped, up to 3 lines, then ellipsized) with a
 * dismiss × to clear the slot.
 *
 * Returns `null` when the store has no selection — the composer
 * renders unchanged in that case.
 *
 * Operator brief 2026-06-28: the visible chat message stays just what
 * the user typed; the chip's text is sent INVISIBLY as system context
 * on send via `runtime.system.enqueue_event`. The chip itself is the
 * user's only feedback that the selection is in play.
 *
 * **Filename is NOT shown** (operator follow-up): long filenames
 * (e.g. `notificacao-vicio-cadeiras__notificacao__20260628-005354.pdf`)
 * dominated the chip in single-line truncation mode and squeezed out
 * the actual selection preview. The lawyer already knows what they
 * selected — the chip is for confirming the *content*, not the
 * source. The source still travels invisibly with the directive (see
 * `buildSelectionDirective`'s `source="…"` attribute) and is exposed
 * via `data-source` for tests/observability.
 */

import { Quote, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  buildSelectionPreview,
  useSelectionContextStore,
} from "@/stores/useSelectionContextStore";
import { cn } from "@/lib/utils";

export function SelectionContextChip() {
  const { t } = useTranslation();
  const selection = useSelectionContextStore((s) => s.selection);
  const clear = useSelectionContextStore((s) => s.clear);

  if (!selection) return null;

  return (
    <div
      data-testid="selection-context-chip"
      data-source={selection.source}
      data-char-count={selection.charCount}
      data-truncated={selection.truncated || undefined}
      className={cn(
        // Block-level chip so wrapping selections grow vertically
        // instead of overflowing horizontally. `self-start` keeps
        // short chips compact (don't span the full composer width).
        "flex max-w-full items-start gap-2 self-start rounded-md",
        "border border-gold-300/70 bg-gold-50 px-2 py-1.5 text-xs",
        "text-coffee-700 dark:border-gold-700/40 dark:bg-gold-950/30 dark:text-cream-300",
      )}
    >
      {/* Quote icon stands in for the "SELECTED" label (operator
          follow-up: text label ate vertical space; icon is enough —
          the lawyer figures it out). `aria-label` keeps the chip
          announced as "selected text" to screen readers. */}
      <Quote
        size={12}
        aria-label={t("composer.selection.label")}
        className="mt-[3px] shrink-0 text-gold-700 dark:text-gold-400"
      />
      {/* Preview capped at ~100 chars with middle-ellipsis (50 head +
          … + 49 tail = 100) so both ends of the selection are always
          visible — useful when the lawyer wants to confirm the
          selection's start AND end without losing either to a
          tail-only truncation. `title` exposes the full text on
          hover. Operator brief 2026-06-28 (4th refinement).
          NOTE: `whitespace-pre-wrap break-words` — at 100 chars the
          preview won't fit on one line in most composer widths, so
          we wrap. `line-clamp-2` caps the visual height at 2 lines
          (browser-rendered ellipsis if the wrapped text still
          exceeds it on very narrow widths). `break-words` lets long
          word sequences wrap mid-word so a URL or 30-char identifier
          can't push the chip past the composer width. */}
      <span
        className="min-w-0 flex-1 whitespace-pre-wrap break-words italic line-clamp-2 text-coffee-700 dark:text-cream-200"
        title={selection.text}
      >
        <PreviewWithTruncationMark
          preview={buildSelectionPreview(selection.text, 50, 49)}
        />
      </span>
      <button
        type="button"
        onClick={clear}
        data-testid="selection-context-chip-dismiss"
        aria-label={t("composer.selection.dismiss")}
        title={t("composer.selection.dismiss")}
        className="mt-[2px] shrink-0 rounded text-gold-700 transition-colors hover:bg-gold-100 hover:text-coffee-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500/40 dark:text-gold-400 dark:hover:bg-gold-900/40 dark:hover:text-cream-100"
      >
        <X size={12} />
      </button>
    </div>
  );
}

/**
 * Renders the middle-ellipsis preview with a visually-distinctive
 * truncation mark in place of the raw `…` character. Operator
 * feedback 2026-06-28 (5th refinement): the bare `…` looked like a
 * typo blending into the italic body text; readers need a clear
 * "[cut]" signal so they instantly recognize the truncation.
 *
 * Visual choice: `[⋯]` — square brackets are the universal editorial
 * "this is an excerpt with a cut" mark; `⋯` (U+22EF MIDLINE
 * HORIZONTAL ELLIPSIS) is vertically centered (cleaner against the
 * italic body baseline than `…`'s descender-aligned dots). The
 * marker is styled non-italic + bold + gold (matches the chip's
 * accent border) so it stands out against the italic gray body.
 * Hair-spaces around it give breathing room without committing a
 * full word-space.
 *
 * When the preview is short enough that `buildSelectionPreview`
 * returned it whole (no `…` insertion), this renders the text
 * verbatim — no marker.
 */
function PreviewWithTruncationMark({ preview }: { preview: string }) {
  const idx = preview.indexOf("…");
  if (idx < 0) return <>{preview}</>;
  const head = preview.slice(0, idx);
  const tail = preview.slice(idx + 1);
  return (
    <>
      {head}
      <span
        aria-label="truncated"
        className="not-italic font-bold text-gold-700 dark:text-gold-300"
      >
        &#x2009;[⋯]&#x2009;
      </span>
      {tail}
    </>
  );
}
